import { Component, Inject, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BankStore, ContactStore } from '../../../core/stores/base.store';
import { Bank, Loan } from '../../../core/models';
import { LoanUtils, NotificationUtils } from '../../../core/utils';
import { formatDateAsYYYYMMDD, parseYYYYMMDDToDate } from 'app/core/utils/date.utils';
import { NumberFormatDirective } from '../../../core/utils/comma.utils';

@Component({
  selector: 'app-loan-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    NumberFormatDirective
  ],
  templateUrl: './loan-payment-dialog.component.html',
  styleUrls: ['./loan-payment-dialog.component.css']
})
export class LoanPaymentDialogComponent implements OnInit {
  paymentForm!: FormGroup;
  loading = false;
  
  private bankStore = inject(BankStore);
  private fb = inject(FormBuilder);
  
  constructor(
    public dialogRef: MatDialogRef<LoanPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { loan: Loan, bank: Bank }
  ) {}
  
  ngOnInit(): void {
    // Get the next payment date from the loan or use current date as fallback
    let nextPaymentDate: Date;
    if (this.data.loan.next_payment_date) {
      // Use our utility to parse the date to ensure correct handling
      // Make sure we're passing a string to parseYYYYMMDDToDate
      const dateStr = typeof this.data.loan.next_payment_date === 'string' ?
        this.data.loan.next_payment_date : String(this.data.loan.next_payment_date);
      const parsedDate = parseYYYYMMDDToDate(dateStr);
      nextPaymentDate = parsedDate || new Date();
    } else {
      nextPaymentDate = new Date();
    }
    
    // Get the next payment number or default to 1
    const nextPaymentNumber = this.data.loan.next_payment_number || 1;
    
    // Get payment amount or calculate it if not available
    let nextPaymentAmount = 0;
    let nextPrincipal = 0;
    let nextInterest = 0;
    
    // Make sure we have a valid payment amount
    if (typeof this.data.loan.payment === 'number' && this.data.loan.payment > 0) {
      nextPaymentAmount = this.data.loan.payment;
    } else if (typeof this.data.loan.payment === 'string') {
      // Try to parse string payment amount
      const parsedAmount = parseFloat(this.data.loan.payment);
      if (!isNaN(parsedAmount) && parsedAmount > 0) {
        nextPaymentAmount = parsedAmount;
      }
    }
        
    // If next_principal and next_interest are available, use them
    if (typeof this.data.loan.next_principal === 'number' && 
        typeof this.data.loan.next_interest === 'number') {
      nextPrincipal = this.data.loan.next_principal;
      nextInterest = this.data.loan.next_interest;
    } else {
      // Otherwise calculate them using LoanUtils
      const breakdown = LoanUtils.getAmortizationPayment(this.data.loan, nextPaymentNumber, true);
      nextPrincipal = breakdown.principal;
      nextInterest = breakdown.interest;
      
      // If payment amount wasn't set, calculate it
      if (nextPaymentAmount === 0) {
        nextPaymentAmount = nextPrincipal + nextInterest;
      }
    }
    
    // Store the original interest amount for reference
    this.originalInterestAmount = nextInterest;
    
    // Initialize the payment form with prepopulated data
    this.paymentForm = this.fb.group({
      date: [nextPaymentDate, Validators.required],
      payment: [nextPaymentAmount.toFixed(2), [Validators.required, Validators.min(0.01)]],
      principal: [nextPrincipal.toFixed(2), [Validators.required]],
      interest: [nextInterest.toFixed(2), [Validators.required]],
      late_fee: ['0.00', [Validators.required, Validators.min(0)]]
    });
    
    // Subscribe to changes in the payment amount to update the principal
    this.paymentForm.get('payment')?.valueChanges.subscribe(value => {
      this.updatePrincipalBasedOnPayment(value);
    });
    
    // Subscribe to changes in the principal amount to update the payment
    this.paymentForm.get('principal')?.valueChanges.subscribe(value => {
      this.updatePaymentBasedOnPrincipal(value);
    });
  }
  
  /**
   * Store the original interest amount to use as reference
   * when calculating the new principal amount
   */
  private originalInterestAmount: number = 0;
  
  /**
   * Updates the principal amount based on the payment amount
   * The interest amount remains fixed, and any change to the payment
   * is applied to the principal
   */
  private updatePrincipalBasedOnPayment(paymentValue: string): void {
    // Parse the payment value
    const paymentAmount = LoanUtils.parseFormattedNumber(paymentValue) || 0;
    
    // Get the current interest value (should be fixed)
    const interestAmount = this.originalInterestAmount;
    
    // Calculate the new principal amount (payment - interest)
    const newPrincipalAmount = Math.max(0, paymentAmount - interestAmount);
    
    // Update the principal field without triggering another update cycle
    this.paymentForm.get('principal')?.setValue(newPrincipalAmount.toFixed(2), { emitEvent: false });
  }
  
  /**
   * Updates the payment amount based on the principal amount
   * The interest amount remains fixed, and the payment is the sum of principal and interest
   */
  private updatePaymentBasedOnPrincipal(principalValue: string): void {
    // Parse the principal value
    const principalAmount = LoanUtils.parseFormattedNumber(principalValue) || 0;
    
    // Get the current interest value (should be fixed)
    const interestAmount = this.originalInterestAmount;
    
    // Calculate the new payment amount (principal + interest)
    const newPaymentAmount = principalAmount + interestAmount;
    
    // Update the payment field without triggering another update cycle
    this.paymentForm.get('payment')?.setValue(newPaymentAmount.toFixed(2), { emitEvent: false });
  }
  
  /**
   * Handle form submission to record a loan payment
   */
  onSubmit(): void {
    if (this.paymentForm.invalid) {
      return;
    }
    
    this.loading = true;
    
    try {
      // Parse form values
      const paymentAmount = LoanUtils.parseFormattedNumber(this.paymentForm.get('payment')?.value) || 0;
      
      // Get the payment date and ensure it's a proper Date object
      const rawPaymentDate = this.paymentForm.get('date')?.value;
      // Make sure we have a proper Date object by setting it to noon to avoid timezone issues
      const paymentDate = new Date(rawPaymentDate);
      paymentDate.setHours(12, 0, 0, 0);
      
      const principal = LoanUtils.parseFormattedNumber(this.paymentForm.get('principal')?.value) || 0;
      const interest = LoanUtils.parseFormattedNumber(this.paymentForm.get('interest')?.value) || 0;
      const lateFee = LoanUtils.parseFormattedNumber(this.paymentForm.get('late_fee')?.value) || 0;
      
      // Create a copy of the loan to avoid modifying the original
      const updatedLoan = { ...this.data.loan };
      
      // Ensure we have a remaining balance
      if (!updatedLoan.remaining_balance) {
        updatedLoan.remaining_balance = updatedLoan.amount;
      }
      
      // Get the next payment number
      const paymentNumber = updatedLoan.next_payment_number || 1;
      
      // Update the loan's remaining balance
      updatedLoan.remaining_balance = Math.max(0, (updatedLoan.remaining_balance - principal));
      
      // Update the loan's last payment date
      if ('last_payment_date' in updatedLoan) {
        updatedLoan.last_payment_date = formatDateAsYYYYMMDD(paymentDate);
      }
      
      // Calculate the next payment date
      // First get the next payment date as a Date object
      const nextPaymentDateObj = LoanUtils.calculateNextPaymentDateAsDate(paymentDate, updatedLoan.frequency);
      // Then format it as YYYY-MM-DD
      updatedLoan.next_payment_date = formatDateAsYYYYMMDD(nextPaymentDateObj);
      
      // Increment the next payment number
      updatedLoan.next_payment_number = paymentNumber + 1;
      
      // Calculate the next payment breakdown
      const nextBreakdown = LoanUtils.getAmortizationPayment(updatedLoan, updatedLoan.next_payment_number, true);
      updatedLoan.next_principal = nextBreakdown.principal;
      updatedLoan.next_interest = nextBreakdown.interest;
      
      // Create the transaction object
      const transaction = {
        bank_id: updatedLoan.bank_id,
        loan_id: updatedLoan.id,
        transaction_date: formatDateAsYYYYMMDD(paymentDate),
        amount: paymentAmount,
        category: 'Loan payment',
        contact_id: updatedLoan.contact_id,
        principal,
        interest,
        late_fee: lateFee,
        loan_amortization_number: paymentNumber
      };

      // Close the dialog with the result
      this.dialogRef.close({
        updatedLoan: updatedLoan, // Use consistent property name
        transaction: transaction
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      const notificationUtils = NotificationUtils.getInstance();
      notificationUtils.showError('Failed to process payment. Please try again.');
      this.loading = false;
    }
  }
  
  /**
   * Cancel the payment dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }
  
}
